import {MiniSignal} from "p3";
import definitions from "../../definitions";
import Shadow from "./misc/Shadow";
import Entity from "../Entity";

export default class Actor extends Entity {
    /**
     * @param {string} id
     */
    constructor(id) {
        super();

        this._id = id;

        this.uid = undefined;
        this.zSort = true;
        this.collisionEnabled = true;

        this.velocity = new P3.Vector2();
        this.linearDamping = 0;
        this.disableKinematic = false;

        this.interactable = false;
        this.interactionRadius = 240;

        this._inInteractionRange = false;
        this._spineListener = null;
        this._shadow = null;
 
        this.signals.interact = new MiniSignal();
    }

    init() {
        super.init();

        this.on("click", this.onClick, this);
        this.on("tap", this.onClick, this);
    }

    destroy() {
        this.removeSpineListener();
        this.removeShadow();

        super.destroy();
    }

    update() {
        super.update();

        if (!this.disableKinematic) {
            this.velocity.x *= Math.exp(-this.linearDamping * window.dt);
            this.velocity.y *= Math.exp(-this.linearDamping * window.dt);

            this.x += this.velocity.x * window.dt;
            this.y += this.velocity.y * window.dt;

            if (Math.abs(this.velocity.x) < 0.1) {
                this.velocity.x = 0;
            }
            if (Math.abs(this.velocity.y) < 0.1) {
                this.velocity.y = 0;
            }
        }

        this.updateShadow();
    }

    /**
     * @param {number} size
     * @param {number} [scale=1]
     * @param {PIXI.Point} [offset=new PIXI.Point()]
     */
    addShadow(size, scale= 1, offset = new PIXI.Point()) {
        if (this.world) {
            this._shadow = new Shadow(size);
            this._shadow.scale.set(scale);
            this._shadow.offset.set(offset.x, offset.y);
            this._shadow.renderable = this._enabled;
            this.world.addEntity(this._shadow, definitions.WORLD_LAYER_TYPE.SHADOW);
            return true;
        }
        console.warn("[" + this.constructor.name + "]", "Failed to add shadow, 'World' is null");
        return false;
    }

    /**
     * @param {PIXI.Texture} texture
     * @param {number} [scale=1]
     * @param {PIXI.Point} [offset=new PIXI.Point()]
     */
    addCustomShadow(texture, scale = 1, offset = new PIXI.Point()) {
        this.addShadow(Shadow.SIZE_CUSTOM, scale, offset);
        this._shadow.setTexture(texture);
    }

    removeShadow() {
        if (this._shadow) {
            this._shadow.destroyNextFrame = true;
            this._shadow = null;
        }
    }

    updateShadow() {
        if (this._shadow) {
            const x = this.x + this._shadow.offset.x * this.scale.x;
            const y = this.y + this._shadow.offset.y;
            this._shadow.position.set(x, y);
        }
    }

    /**
     * @param {Event} event
     * @private
     */
    onClick(event) {
        if (this.inInteractionRange) {
            this.onInteractWith(null);
        }
    }

    /**
     * @param {Actor} actor
     */
    onInteractWith(actor) {
        this.signals.interact.dispatch(this, actor);
    }

    onInteractionRangeEnter() {
        if (this.enabled) {
            this._inInteractionRange = true;
    
            this.interactive = true;
            this.buttonMode = true;
        }
    }

    onInteractionRangeUpdate() {}

    onInteractionRangeExit() {
        if (this.enabled) { 
            this._inInteractionRange = false;
            
            this.interactive = false;
            this.buttonMode = false;
        }
    }

    onEnable() {
        super.onEnable();

        if (this._shadow) {
            this._shadow.renderable = true;
        }
    }

    onDisable() {
        super.onDisable();

        if (this._shadow) {
            this._shadow.renderable = false;
        }
    }

    /**
     * @returns {string}
     */
    get id() {
        return this._id;
    }

    /**
     * @return {boolean}
     */
    get inInteractionRange() {
        return this._inInteractionRange;
    }
}